import { Component, ViewChild } from '@angular/core';
import { EmployeeJobTitleService } from './employee-job-title.service';
import { EmployeeJobTitleModel } from './model/employee-job-title.model';
import { MatDialog } from '@angular/material/dialog';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';
import { RequestData, SearchSort } from 'src/app/common/models/request-data.model';
import { BreakpointObserver, BreakpointState, Breakpoints } from '@angular/cdk/layout';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { FilterListItemModel, SearchFilterComponent, SearchParam } from 'src/app/common/components/search-filter/search-filter.component';
import { Router } from '@angular/router';
import { StateManagerService } from 'src/app/common/services/state.manager.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, SortDirection } from '@angular/material/sort';
import { PermissionService } from 'src/app/auth/permission.service';

@Component({
  selector: 'app-employee-job-title',
  templateUrl: './employee-job-title.component.html',
  styleUrls: ['./employee-job-title.component.scss'],
  standalone: false
})
export class EmployeeJobTitleComponent {
  list: EmployeeJobTitleModel[] = [];
  showSearch: boolean = false;
  isLoading = true;
  IsDownloading = false;

  filter: FilterListItemModel[] = [
    { title: "Code", column: "code" },
    { title: "Name", column: "name" },
    { title: "Description", column: "description" },
    { title: "Position", column: "job_position_name" },
    {
      title: "Is HQ", column: "is_hq", type: "boolean",
      values: [
        { title: "Yes", value: "1" },
        { title: "No", value: "0" },
      ]
    },
    {
      title: "Is Active", column: "is_active", type: "boolean",
      values: [
        { title: "Yes", value: "1" },
        { title: "No", value: "0" },
      ]
    },
  ];
  displayedColumns = ['code', 'name', 'description', 'job_position_name', 'is_hq', 'action'];
  pagingData = { length: 0, pageSize: 50, pageIndex: 0 };
  isHandset: boolean = false;
  searchColumFilter: SearchParam = {};
  columnSortData: SearchSort[] = [];
  selectedItem?: EmployeeJobTitleModel;
  dataSource = new MatTableDataSource(this.list);
  permission: any;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('searchFilter') searchFilter!: SearchFilterComponent;

  constructor(private breakpointObserver: BreakpointObserver,
    private service: EmployeeJobTitleService,
    private stateManagerService: StateManagerService,
    public dialog: MatDialog,
    public permissionService: PermissionService,
    private snackBarService: SnackBarService,
    private router: Router) {
    this.permission = permissionService.getPagePermission("job-title");
  }

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.Handset]).subscribe((state: BreakpointState) => {
      this.isHandset = state.matches;
    });
  }

  setDataSource(list: EmployeeJobTitleModel[]) {
    this.dataSource = new MatTableDataSource(list);
    this.dataSource.sort = this.sort;
  }

  ngAfterViewInit(): void {
    this.loadPreviousState();
  }

  loadPreviousState(): void {
    var previousState = this.stateManagerService.getState(this.constructor.name);
    if (previousState !== undefined && this.searchFilter != undefined) {
      if (previousState.state.search.filters.scope) {
        this.pagingData.pageIndex = previousState.state.search.filters.scope.offset / previousState.state.search.filters.scope.limit;
        this.pagingData.pageSize = previousState.state.search.filters.scope.limit;
      }
      if (previousState.state.search.sort != undefined && previousState.state.search.sort.length > 0) {
        this.setSortColumn(previousState.state.search.sort[0].column, previousState.state.search.sort[0].order);
      }
      this.searchColumFilter = JSON.parse(JSON.stringify(previousState.state.search.filters));
      this.searchFilter.setSearchFilter(this.searchColumFilter, true);
    } else {
      this.setSortColumn('name', 'asc');
      this.loadData();
    }
  }

  setSortColumn(column: string, direction: SortDirection): void {
    this.columnSortData.length = 0;
    this.columnSortData.push({ column: column, order: direction });
    if (this.sort) {
      this.sort.sort({ id: column, start: direction, disableClear: false });
    }
  }

  buildRequestData(): RequestData {
    let searchParam = this.searchColumFilter;
    let requestData: RequestData = {
      search: {
        filters: {
          simpleSearch: searchParam.freeSearch,
          advSearch: searchParam.advSearch || [],
          scope: {
            limit: this.pagingData.pageSize,
            offset: (this.pagingData.pageIndex * this.pagingData.pageSize),
          }
        },
        sort: this.columnSortData,
      }
    };
    return requestData;
  }

  onFilterApplied(columFilter: any): void {
    this.searchColumFilter = columFilter;
    this.loadData();
  }

  onDelete(info: EmployeeJobTitleModel): void {
    const message = `Are you sure you want to delete ` + (info.name + "?");
    const dialogData = new ConfirmDialogModel("Confirm Deletion", message);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { maxWidth: "400px", data: dialogData });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.service.delete(info).subscribe({
          next: (response) => {
            if (response.status == 'SUCCESS') {
              this.snackBarService.openSnackBar({ type: 'success', title: 'Deleted', message: 'Record deleted successfully.' });
              this.loadData();
            } else {
              this.snackBarService.openSnackBar({ message: response.message, title: 'Failed!!!', type: 'error' });
            }
          },
          error: () => {
            this.snackBarService.openSnackBar({ message: 'Failed to delete. Connection to the server failed', title: 'Failed!!!', type: 'error' });
          }
        });
      }
    });
  }

  loadData(): void {
    this.isLoading = true;
    let requestData = this.buildRequestData();
    this.service.getList(requestData).subscribe(response => {
      if (response.status == 'SUCCESS') {
        const list = response.data.job_titles || [];
        list.forEach((item: any) => {
          item.is_hq = item.is_hq ? Number(item.is_hq) : 0;
          item.is_active = item.is_active ? Number(item.is_active) : 0;
        });
        this.list = list;
        this.setDataSource(this.list);
        this.pagingData.length = response.data.total_count;
        if (this.pagingData.length > 0) {
          this.stateManagerService.setState(this.constructor.name, requestData);
        }
      } else {
        this.snackBarService.openSnackBar({ message: response.message, title: 'Failed!!!', type: 'error' });
      }
      this.isLoading = false;
    });
  }

  onRowSelected(info: EmployeeJobTitleModel): void {
    this.selectedItem = info;
  }

  sortData($event: any): void {
    if (this.sort) {
      this.columnSortData = [{ column: this.sort.active, order: this.sort.direction }];
      this.loadData();
    }
  }

  onEdit(info: EmployeeJobTitleModel): void {
    this.router.navigate(['system/employee-job-title-edit', info.id]);
  }

  onePageEvent(event: any): void {
    this.pagingData.pageIndex = event.pageIndex;
    this.pagingData.pageSize = event.pageSize;
    this.loadData();
  }

  addClicked(): void {
    this.router.navigate(['system/employee-job-title-edit', 0]);
  }

  exportClicked() {
    if (this.pagingData.length === 0) {
      this.snackBarService.openSnackBar({ message: 'No data found to export.', title: 'No Data!!!', type: 'warn' });
      return;
    }
    this.isLoading = true;
    this.IsDownloading = true;
    this.service.downloadExcel(this.buildRequestData()).subscribe({
      next: blob => {
        this.IsDownloading = false;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'employee_job_titles.csv';
        link.click();
        window.URL.revokeObjectURL(url);
        this.isLoading = false;
      },
      error: () => {
        this.IsDownloading = false;
        this.snackBarService.openSnackBar({ message: 'Failed to export.', title: 'Failed!!!', type: 'error' });
        this.isLoading = false;
      }
    });
  }
}
